import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Mail, ArrowRight, Stethoscope } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function SignUpSuccessPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('doctor-platform');

  useEffect(() => {
    // Auto-redirect after 10 seconds
    const timer = setTimeout(() => {
      navigate('/auth/login');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('auth.signUpSuccess.title')}</CardTitle>
          <CardDescription className="mt-2">
            {t('auth.signUpSuccess.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Medical Portal Branding */}
          <div className="flex justify-center pb-2">
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Stethoscope className="h-5 w-5" />
              <span className="text-sm font-medium">{t('auth.signUpSuccess.portalLabel')}</span>
            </div>
          </div>

          {/* Email Confirmation Notice */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {t('auth.signUpSuccess.checkEmailTitle')}
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                  {t('auth.signUpSuccess.checkEmailDescription')}
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium">{t('auth.signUpSuccess.nextStepsTitle')}</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>{t('auth.signUpSuccess.nextSteps.email')}</li>
              <li>{t('auth.signUpSuccess.nextSteps.verify')}</li>
              <li>{t('auth.signUpSuccess.nextSteps.profile')}</li>
              <li>{t('auth.signUpSuccess.nextSteps.approval')}</li>
              <li>{t('auth.signUpSuccess.nextSteps.portal')}</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/auth/login')}
              className="w-full"
            >
              {t('auth.signUpSuccess.goToLogin')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <div className="text-center text-xs text-muted-foreground">
              <p>{t('auth.signUpSuccess.missingEmail')}</p>
              <button
                onClick={() => navigate('/auth/signup')}
                className="text-primary hover:underline"
              >
                {t('auth.signUpSuccess.tryAgain')}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
